import React from 'react';
import CompareShell, { AppStoreBadges } from './CompareShell';
import { Check, X, Users, Receipt, Zap, ArrowRight, DollarSign, Smartphone, CreditCard } from 'lucide-react';

interface MasterRow {
  name: string;
  clero: string;
  others: string;
  isDifferent?: boolean;
}

interface MasterSection {
  category: string;
  features: MasterRow[];
}

const GENERAL_APPS_MATRIX: MasterSection[] = [
  {
    category: 'Group Spending & Cycles',
    features: [
      {
        name: 'Monthly roommate cycles (rent & recurring bills)',
        clero: 'Auto-settles on the 1st',
        others: 'Manual reminders or unsupported',
        isDifferent: true,
      },
      {
        name: 'Trip & vacation groups',
        clero: 'Auto-settles on trip end date',
        others: 'Manual ledger or unsupported',
        isDifferent: true,
      },
      {
        name: 'On-demand running group tabs',
        clero: '1-tap settle anytime',
        others: 'Manual tracking only',
        isDifferent: true,
      },
      {
        name: 'Group balance simplification (minimize transfers)',
        clero: 'Included ($0 free)',
        others: 'Paywalled ($39/yr) or unsupported',
        isDifferent: true,
      },
      {
        name: '1-tap batch payment for all open requests',
        clero: '1 bank transfer',
        others: 'Authorize 1-by-1 or external',
        isDifferent: true,
      },
      {
        name: 'Locked paid shares protection',
        clero: 'Permanently locked',
        others: 'Past edits permitted or unsupported',
        isDifferent: true,
      },
    ],
  },
  {
    category: 'Receipts & Item Splitting',
    features: [
      {
        name: 'AI camera receipt scanner',
        clero: 'Free & unlimited',
        others: 'Subscription required or manual entry',
        isDifferent: true,
      },
      {
        name: 'Gmail & email receipt sync',
        clero: 'Auto-finds Uber & food orders',
        others: 'Not supported',
        isDifferent: true,
      },
      {
        name: 'Friend web claiming (no app install needed)',
        clero: '1-tap web link',
        others: 'Requires app install & account',
        isDifferent: true,
      },
      {
        name: 'Smart card charge requests',
        clero: '1-tap from transactions',
        others: 'Manual typing required',
        isDifferent: true,
      },
      {
        name: 'Exact cent proportional tax & tip split',
        clero: 'Proportional to the cent',
        others: 'Supported or manual math',
        isDifferent: false,
      },
    ],
  },
  {
    category: 'Money Movement & Bank Settlement',
    features: [
      {
        name: 'Direct bank checking deposit',
        clero: 'Same-day direct deposit*',
        others: 'Trapped in app wallet or no payments',
        isDifferent: true,
      },
      {
        name: 'Instant transfer & cashout fees',
        clero: '$0 always',
        others: '1.75% to 2% cashout penalties',
        isDifferent: true,
      },
      {
        name: 'Standard transfer time',
        clero: 'Same-day direct to bank*',
        others: '1 to 3 business days',
        isDifferent: true,
      },
      {
        name: 'Multi-bank & card account sync',
        clero: 'Multiple banks & cards',
        others: 'Single primary account or none',
        isDifferent: true,
      },
      {
        name: 'Friend web payment portal',
        clero: 'Pay on web without app',
        others: 'Requires specific app installation',
        isDifferent: true,
      },
    ],
  },
  {
    category: 'Pricing, Privacy, & Experience',
    features: [
      {
        name: 'Annual subscription fee',
        clero: '$0 (100% free)',
        others: 'Up to $39.99/year or fees on transfers',
        isDifferent: true,
      },
      {
        name: 'In-app banner ads',
        clero: '100% ad-free',
        others: 'Banner ads or sponsored promos',
        isDifferent: true,
      },
      {
        name: 'Public social transaction feed',
        clero: 'None (100% private)',
        others: 'Public feed default or shared groups',
        isDifferent: true,
      },
      {
        name: 'Biometric login protection',
        clero: 'Face ID & Touch ID',
        others: 'Varies by app',
        isDifferent: false,
      },
    ],
  },
];

export const CompareHubPage: React.FC = () => {
  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  };

  return (
    <CompareShell currentPageTitle="Payment & Expense Splitting Apps Comparison">
      {/* STRUCTURED JSON-LD SEO SCHEMA */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Best Payment & Expense Splitting Apps: 2026 Comparison Guide',
            description:
              'See how Clero compares against traditional payment apps, digital wallets, and bill splitting tools. Direct bank deposits, AI receipts, and group balance netting.',
            url: 'https://clero.money/compare',
          }),
        }}
      />

      {/* HERO SECTION (FLIGHTY STYLE) */}
      <section className="pt-6 pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-slate-950">
            Payment & Splitting Apps
          </h1>
          <p className="mx-auto max-w-2xl text-base sm:text-lg text-slate-600 font-medium leading-relaxed">
            See how Clero compares against traditional payment apps, digital wallets, and bill splitting tools.
          </p>

          {/* HERO DOWNLOAD ACTION (FLIGHTY STYLE) */}
          <div className="pt-2 flex items-center justify-center">
            <AppStoreBadges badgeHeightClass="h-11 sm:h-12" />
          </div>
        </div>
      </section>

      {/* MASTER COMPARISON MATRIX (FLIGHTY STYLE) */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl">
          {/* COMPREHENSIVE FLIGHTY-STYLE WRITE-UP */}
          <div className="rounded-3xl bg-white p-8 sm:p-12 mb-10 space-y-8">
            <div className="border-b border-slate-100 pb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                The modern standard for shared living and group payments
              </h2>
              <p className="mt-3 text-base text-slate-600 font-medium leading-relaxed">
                Managing shared expenses has traditionally required juggling multiple disconnected tools: offline calculators to track who owes what, digital wallets that hold your cash, and bank apps that only handle simple 1-on-1 transfers.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">1. Tracking combined with bank settlement</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Traditional IOU apps stop at math, forcing you to chase payments across separate payment apps. Clero combines automated expense itemization with same-day* direct bank transfers, locking paid shares permanently for $0.
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">2. $0 direct deposits vs cashout fees</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Digital wallet middlemen trap incoming money in app balances and charge 1.75% to 2% penalties to cash out immediately. Clero deposits directly into your checking account on the same day* with $0 transfer or cashout fees.
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">3. Multi-party group intelligence</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Standard bank P2P tools only handle isolated 1-on-1 transfers without receipt scanning or group tabs. Clero introduces Monthly roommate cycles, Trip vacation groups, AI camera receipt scanning, and 1-tap batch settlements.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
              Feature breakdown
            </h2>
            <p className="mt-2 text-sm text-slate-600 font-medium">
              Side-by-side comparison of Clero vs other payment and splitting apps.
            </p>
          </div>

          <div className="overflow-hidden rounded-3xl bg-white">
            {/* TABLE TOP HEADER WITH APP LOGOS */}
            <div className="grid grid-cols-12 gap-3 px-6 py-4 border-b border-slate-100 items-center bg-slate-50/50">
              <div className="col-span-6 sm:col-span-5">
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Features</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <img src="/clero_icon.png" alt="Clero" className="h-6 w-6 rounded-lg shrink-0 object-cover shadow-sm" />
                <span className="font-extrabold text-teal-950 text-xs sm:text-sm">Clero</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <span className="font-bold text-slate-700 text-xs sm:text-sm">Other Apps</span>
              </div>
            </div>

            {/* MATRIX CATEGORIES & CONCISE ROWS */}
            {GENERAL_APPS_MATRIX.map((section) => (
              <div key={section.category}>
                <div className="bg-slate-100/70 px-6 py-2.5">
                  <h3 className="text-[11px] font-extrabold uppercase tracking-wider text-slate-800">
                    {section.category}
                  </h3>
                </div>

                <div className="divide-y divide-slate-100">
                  {section.features.map((row) => (
                    <div
                      key={row.name}
                      className="grid grid-cols-12 gap-3 px-6 py-3.5 items-center hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="col-span-6 sm:col-span-5">
                        <span className="text-xs sm:text-sm font-bold text-slate-900 block">{row.name}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 bg-teal-50/40 p-2 rounded-xl">
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
                          <Check className="h-2.5 w-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs font-bold text-teal-950">{row.clero}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 p-2">
                        {row.isDifferent ? (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <X className="h-2.5 w-2.5 stroke-[2.5]" />
                          </div>
                        ) : (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                            <Check className="h-2.5 w-2.5 stroke-[3]" />
                          </div>
                        )}
                        <span className="text-xs font-medium text-slate-600">{row.others}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* MID-PAGE CONVERSION CALLOUT (FLIGHTY STYLE) */}
          <div className="mt-8 rounded-3xl bg-white p-6 sm:p-8 text-center space-y-3">
            <h3 className="text-lg sm:text-xl font-black text-slate-950">
              One app for shared living and group payments
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
              Direct bank deposits, AI receipt scanning, and group balance netting with $0 fees.
            </p>
            <div className="flex justify-center pt-1">
              <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
            </div>
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS (FLIGHTY STYLE) */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl rounded-3xl bg-white p-7 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-6 text-sm sm:text-base text-slate-700 font-medium divide-y divide-slate-100">
            <div className="pt-4 first:pt-0">
              <h3 className="text-base font-bold text-slate-950">What makes Clero different from other payment and splitting apps?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Clero combines group expense tracking with real money movement. Instead of tracking IOUs in one app and paying in another, Clero automatically calculates proportional shares, itemizes receipts with AI, and moves funds directly between bank accounts on the same day* with $0 fees.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">How do Clero Groups work for roommates and trips?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Clero offers three purpose-built group modes: <strong>Monthly</strong> groups for roommates (auto-settling rent and utilities on the 1st), <strong>Trip</strong> groups for vacations (auto-closing on your return date), and <strong>On-Demand</strong> running tabs with friends.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Do my friends need to install Clero to pay their share?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. Clero generates a secure web payment link. Friends can view their exact items, adjust tips, and pay securely through their browser without downloading an app or creating a new account.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Are there any hidden transfer fees or subscriptions?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. Clero is 100% free. There are no monthly subscriptions, no fees on standard or instant direct bank deposits, and no limits on AI receipt scanning.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">How secure is my financial data with Clero?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Clero connects to your bank via Plaid using 256-bit bank-grade encryption. Clero never stores your bank credentials or card numbers, and all payments require biometric verification (Face ID or Touch ID).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DIRECT COMPARISONS JUMP */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <span className="text-xs font-extrabold tracking-widest uppercase text-slate-500">
            Direct 1-on-1 comparisons
          </span>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/compare/splitwise')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Splitwise</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
            <button
              onClick={() => navigate('/compare/venmo')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Venmo</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
            <button
              onClick={() => navigate('/compare/zelle')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Zelle</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
          </div>
        </div>
      </section>
    </CompareShell>
  );
};
export default CompareHubPage;
